type RouteType =
  | 'PodcastEntityResponse'
  | 'MeditationEntityResponse'
  | 'CourseEntityResponse'
  | 'ResourceEntityResponse'
  | 'InsightEntityResponse'
  | 'MindfulMinuteEntityResponse'
  | 'CalendarEntityResponse'
  | 'EmotionWheel';

type RouteResult = {
  type: RouteType;
  slug?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseTextWithLinks(text: string, finalState: any, className?: string): string {
  if (!text) return '';

  const linkRegex = new RegExp('{{\\[(.*?)\\]\\[(.*?)\\]}}', 'g');

  const parsedText = text.replace(linkRegex, (_, linkText, reference) => {
    const routeData = getValidRouteByReference(finalState, reference);

    if (!routeData) return linkText;

    const url = buildRouteUrl(routeData);

    return `<a href="${url}" class="font-bold underline ${className || ''}">${linkText}</a>`;
  });

  return `<p class="text-center text-sh-xxs ${className || ''}">${parsedText}</p>`;
}

export function buildRouteUrl(routeData: { type: string; slug?: string }) {
  switch (routeData.type) {
    case 'PodcastEntityResponse':
      return `/podcast/${routeData.slug}`;
    case 'MeditationEntityResponse':
      return `/mindful-exercise/${routeData.slug}`;
    case 'CourseEntityResponse':
      return `/learn/course/${routeData.slug}`;
    case 'ResourceEntityResponse':
      return `/knowledge-hub/slug/${routeData.slug}`;
    case 'MindfulMinuteEntityResponse':
      return `/mindful-minute`;
    case 'InsightEntityResponse':
      return `/daily-insight`;
    case 'CalendarEntityResponse':
      return `/wellbeing-awareness-days-calendar`;
    case 'EmotionWheel':
      return `/emotion-wheel`;
    default:
      return '#';
  }
}

export function getValidRouteByReference(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  finalState: any,
  reference: string,
): RouteResult | null {
  if (reference === 'podcast' && finalState?.podcast?.data?.attributes?.slug && finalState?.podcast?.__typename) {
    return {
      type: finalState.podcast.__typename,
      slug: finalState.podcast.data.attributes.slug,
    };
  }

  if (
    reference === 'mindfulExercise' &&
    finalState?.mindfulExercise?.data?.attributes?.slug &&
    finalState?.mindfulExercise?.__typename
  ) {
    return {
      type: finalState.mindfulExercise.__typename,
      slug: finalState.mindfulExercise.data.attributes.slug,
    };
  }

  if (reference === 'course' && finalState?.course?.data?.attributes?.slug && finalState?.course?.__typename) {
    return {
      type: finalState.course.__typename,
      slug: finalState.course.data.attributes.slug,
    };
  }

  if (reference === 'resource' && finalState?.resource?.data?.attributes?.slug && finalState?.resource?.__typename) {
    return {
      type: finalState.resource.__typename,
      slug: finalState.resource.data.attributes.slug,
    };
  }

  if (reference === 'redirectToCurrentInsight' && finalState?.redirectToCurrentInsight) {
    return {
      type: 'InsightEntityResponse',
    };
  }

  if (reference === 'redirectToCurrentMindfulMinute' && finalState?.redirectToCurrentMindfulMinute) {
    return {
      type: 'MindfulMinuteEntityResponse',
    };
  }

  if (reference === 'redirectToCalendar' && finalState?.redirectToCalendar) {
    return {
      type: 'CalendarEntityResponse',
    };
  }

  if (reference === 'redirectToEmotionWheel' && finalState?.redirectToEmotionWheel) {
    return {
      type: 'EmotionWheel',
    };
  }

  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFirstValidRoute(finalState: any): RouteResult | null {
  const routeChecks: { type: RouteType; condition: boolean; slug?: string }[] = [
    {
      type: finalState?.podcast?.__typename as RouteType,
      condition: !!finalState?.podcast?.data?.attributes?.slug,
      slug: finalState?.podcast?.data?.attributes?.slug,
    },
    {
      type: finalState?.mindfulExercise?.__typename as RouteType,
      condition: !!finalState?.mindfulExercise?.data?.attributes?.slug,
      slug: finalState?.mindfulExercise?.data?.attributes?.slug,
    },
    {
      type: finalState?.course?.__typename as RouteType,
      condition: !!finalState?.course?.data?.attributes?.slug,
      slug: finalState?.course?.data?.attributes?.slug,
    },
    {
      type: finalState?.resource?.__typename as RouteType,
      condition: !!finalState?.resource?.data?.attributes?.slug,
      slug: finalState?.resource?.data?.attributes?.slug,
    },
    {
      type: 'InsightEntityResponse',
      condition: !!finalState?.redirectToCurrentInsight,
    },
    {
      type: 'MindfulMinuteEntityResponse',
      condition: !!finalState?.redirectToCurrentMindfulMinute,
    },
    {
      type: 'CalendarEntityResponse',
      condition: !!finalState?.redirectToCalendar,
    },
    {
      type: 'EmotionWheel',
      condition: !!finalState?.redirectToEmotionWheel,
    },
  ];

  const foundRoute = routeChecks.find((route) => route.condition);

  return foundRoute ? { type: foundRoute.type, slug: foundRoute.slug } : null;
}
